import React, { useEffect } from 'react';
import { ICompany, OpenViewDetailsPropTypes } from '../../types';
import { IElement } from 'lib/common/types';
import { MyRequestDetailsPage } from './my-requests-details-page';
import { RequestListPage } from './my-requests-list-page';
import { AnalystReports } from 'modules/analyst-reports';
import { RequestForm } from 'modules/requests/components/RequestForm';
import { BulkRequestForm } from 'modules/requests/components/BulkRequestForm';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { UserApi } from 'lib/api/user';
import { useAuth } from 'modules/auth/context/auth.consumer';
import { CompanyApi } from 'lib/api/company';
import { RequestApi } from 'lib/api/requests';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export function MyRequestsPageRoot() {
  const [isDetailsView, setIsDetailsView] = React.useState(false);
  const [detailsData, setDetailsData] = React.useState(
    {} as OpenViewDetailsPropTypes,
  );
  const [value, setValue] = React.useState(0);
  const [requestMode, setRequestMode] = React.useState<'individual' | 'bulk'>(
    'individual',
  );
  const [isBulkEnabled, setIsBulkEnabled] = React.useState(false);
  const [featuresLoaded, setFeaturesLoaded] = React.useState(false);
  const { user } = useAuth();
  const [userSectors, setUserSectors] = React.useState<IElement[]>();
  const [userInstruments, setUserInstruments] = React.useState<IElement[]>();
  const [company, setCompany] = React.useState<ICompany>();

  const handleViewTest = (data: OpenViewDetailsPropTypes) => {
    setDetailsData(data);
    setIsDetailsView(true);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const fecthData = async () => {
    if (user) {
      const completeUser = await UserApi.getUser(user.id);

      const company = await CompanyApi.getCompany(completeUser.company.id);

      setUserSectors(completeUser.sectors);
      setUserInstruments(completeUser.instruments ?? []);
      setCompany(company);
    }
  };

  useEffect(() => {
    RequestApi.getFeatures()
      .then((features) => setIsBulkEnabled(features.bulkRequestEnabled))
      .catch(() => setIsBulkEnabled(false))
      .finally(() => setFeaturesLoaded(true));
  }, []);

  useEffect(() => {
    fecthData();
  }, [user]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleTabChange}
          aria-label="basic tabs example"
        >
          <Tab label="Solicitar Avaliações" {...a11yProps(0)} />
          <Tab label="Minhas Solicitações" {...a11yProps(1)} />

          <Tab label="Relatórios" {...a11yProps(2)} />
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Button
            variant={requestMode === 'individual' ? 'contained' : 'outlined'}
            onClick={() => setRequestMode('individual')}
            sx={{
              borderRadius:
                featuresLoaded && isBulkEnabled ? '4px 0 0 4px' : '4px',
              textTransform: 'none',
              fontWeight: requestMode === 'individual' ? 700 : 400,
            }}
          >
            Solicitação individual
          </Button>
          {featuresLoaded && isBulkEnabled && (
            <Button
              variant={requestMode === 'bulk' ? 'contained' : 'outlined'}
              onClick={() => setRequestMode('bulk')}
              sx={{
                borderRadius: '0 4px 4px 0',
                ml: '-1px',
                textTransform: 'none',
                fontWeight: requestMode === 'bulk' ? 700 : 400,
              }}
            >
              Solicitação em massa
            </Button>
          )}
        </Box>

        {requestMode === 'individual' || !isBulkEnabled ? (
          <RequestForm
            company={company}
            sectors={userSectors}
            instruments={userInstruments}
          />
        ) : (
          <BulkRequestForm
            company={company}
            sectors={userSectors}
            instruments={userInstruments}
          />
        )}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {isDetailsView ? (
          <MyRequestDetailsPage
            data={detailsData}
            goToAllRequests={() => setIsDetailsView(false)}
          />
        ) : (
          <RequestListPage onViewDetails={handleViewTest} />
        )}
      </TabPanel>
      <TabPanel value={value} index={2}>
        <AnalystReports />
      </TabPanel>
    </Box>
  );
}
